import React, { useState, useEffect } from 'react';
import DashboardLayout from '../Layout/DashboardLayout';
import { requestHelpers } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import './MediaManager.css';

const MediaManager = () => {
  const { user } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const response = await requestHelpers.get('/media');
      setMedia(response.data || []);
    } catch (error) {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName || file.name);

    try {
      setUploading(true);
      await requestHelpers.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Asset uploaded to WhatsApp successfully');
      setFile(null);
      setFileName('');
      fetchMedia();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Upload failed. Ensure file type is supported by WhatsApp.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove from your local library? (The asset will still exist on WhatsApp for 30 days)')) return;
    try {
      await requestHelpers.delete(`/media/${id}`);
      toast.success('Asset removed');
      setMedia(media.filter(m => m._id !== id));
    } catch (error) {
      toast.error('Failed to remove asset');
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return 'fa-image';
      case 'video': return 'fa-video';
      case 'audio': return 'fa-volume-up';
      default: return 'fa-file-alt';
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <DashboardLayout>
      <div className="media-manager-page">
        <div className="page-header">
          <div className="header-content">
            <h1>Media Library</h1>
            <p>Upload and manage assets for your WhatsApp campaigns</p>
            {user?.whatsappNumber && (
              <div className="header-number-badge mt-2" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                <i className="fab fa-whatsapp"></i>
                <span>Linked to: {user.whatsappNumber}</span>
              </div>
            )}
          </div>
        </div>

        <div className="media-grid-container">
          {/* Upload Section */}
          <div className="upload-card glass">
            <h3><i className="fas fa-cloud-upload-alt"></i> New Upload</h3>
            <form onSubmit={handleUpload} className="upload-form">
              <div 
                className={`drag-area ${dragActive ? 'active' : ''} ${file ? 'has-file' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('fileInput').click()}
              >
                <input 
                  type="file" 
                  id="fileInput" 
                  hidden 
                  onChange={handleFileChange} 
                />
                {!file ? (
                  <div className="drag-content">
                    <i className="fas fa-file-upload"></i>
                    <p>Drag & drop or click to browse</p>
                    <span>Images, Video (mp4), PDF, etc.</span>
                  </div>
                ) : (
                  <div className="file-info">
                    <i className={`fas ${getFileIcon(file.type.split('/')[0])} text-primary`}></i>
                    <p>{file.name}</p>
                    <span>{formatSize(file.size)}</span>
                  </div>
                )}
              </div>

              <div className="form-group mt-3">
                <label>Display Name (Internal Only)</label>
                <input 
                  type="text" 
                  placeholder="E.g. Summer Promo Image"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                />
              </div>

              <button 
                type="submit" 
                className="btn btn--primary btn--block mt-3"
                disabled={uploading || !file}
              >
                {uploading ? (
                  <><i className="fas fa-spinner fa-spin mr-2"></i> Syncing to WhatsApp...</>
                ) : (
                  'Upload to WhatsApp'
                )}
              </button>
            </form>

            <div className="upload-tips mt-4">
              <h4><i className="fas fa-info-circle"></i> Requirements</h4>
              <ul>
                <li>Images: Max 5MB (JPG, PNG)</li>
                <li>Video: Max 16MB (MP4, 3GP)</li>
                <li>Documents: Max 100MB (PDF, DOCX)</li>
                <li>IDs expire automatically after 30 days</li>
              </ul>
            </div>
          </div>

          {/* List Section */}
          <div className="library-section">
            <div className="section-header">
              <h3><i className="fas fa-th-large"></i> Your Media Assets</h3>
              <button className="btn btn--sm btn--outline" onClick={fetchMedia}>
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>

            {loading ? (
              <div className="empty-state">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading library...</p>
              </div>
            ) : media.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-folder-open"></i>
                <p>Your media library is empty</p>
                <span>Upload an asset to use it in your campaigns</span>
              </div>
            ) : (
              <div className="media-items-grid">
                {media.map(item => (
                  <div key={item._id} className={`media-card glass ${item.daysRemaining < 7 ? 'expiring' : ''}`}>
                    <div className="media-type-badge">
                      <i className={`fas ${getFileIcon(item.fileType)}`}></i>
                      {item.fileType}
                    </div>
                    
                    <div className="media-body">
                      <h4 title={item.fileName}>{item.fileName}</h4>
                      <code className="media-id" onClick={() => {
                        const cleanId = item.mediaId.replace(/^HANDLE:/, '');
                        navigator.clipboard.writeText(cleanId);
                        toast.info('Media ID copied to clipboard');
                      }}>
                        {item.mediaId.replace(/^HANDLE:/, '')} <i className="far fa-copy"></i>
                      </code>
                      
                      <div className="media-expiry">
                        <div className="expiry-info">
                          <span>Expires in {item.daysRemaining} days</span>
                          <div className="expiry-bar">
                            <div 
                              className="expiry-progress" 
                              style={{ width: `${(item.daysRemaining / 29) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="media-actions">
                      <button className="delete-btn" onClick={() => handleDelete(item._id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MediaManager;
